/**
 * Phase 11a: Modal for creating / replacing a Schedule of Values.
 *
 * Renders a form to define SOV line items and holdback settings.
 * Validates duplicate line numbers client-side before submitting.
 */
import React, { useState } from 'react';
import { useCreateSOV, type SOVLineItemCreate } from '@/hooks/useSOV';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlusCircle, Trash2, AlertCircle } from 'lucide-react';

interface SOVModalProps {
  projectId: string;
  existingSov?: any;
  onClose: () => void;
}

interface LineItemFormState extends SOVLineItemCreate {
  _key: number; // local key for React list rendering
}

let _keyCounter = 0;
function nextKey() {
  return ++_keyCounter;
}

function defaultLine(lineNumber: number): LineItemFormState {
  return {
    _key: nextKey(),
    line_number: lineNumber,
    description: '',
    cost_code: '',
    original_budget_amount: 0,
    holdback_percent: null,
  };
}

export default function SOVModal({ projectId, existingSov, onClose }: SOVModalProps) {
  const createSOV = useCreateSOV(projectId);

  // Initialise from existing SOV if replacing, otherwise start blank
  const initialLines: LineItemFormState[] = existingSov?.line_items?.length
    ? existingSov.line_items.map((li: any) => ({
        _key: nextKey(),
        line_number: li.line_number,
        description: li.description,
        cost_code: li.cost_code,
        original_budget_amount: Number(li.original_budget_amount),
        holdback_percent: li.holdback_percent != null ? Number(li.holdback_percent) : null,
      }))
    : [defaultLine(1)];

  const [holdbackPercent, setHoldbackPercent] = useState<string>(
    existingSov ? String(existingSov.holdback_percent) : '10'
  );
  const [lenderName, setLenderName] = useState(existingSov?.lender_name ?? '');
  const [loanNumber, setLoanNumber] = useState(existingSov?.loan_number ?? '');
  const [lines, setLines] = useState<LineItemFormState[]>(initialLines);
  const [validationError, setValidationError] = useState<string | null>(null);

  function addLine() {
    const nextNum = lines.length > 0 ? Math.max(...lines.map((l) => l.line_number)) + 1 : 1;
    setLines((prev) => [...prev, defaultLine(nextNum)]);
  }

  function removeLine(key: number) {
    setLines((prev) => prev.filter((l) => l._key !== key));
  }

  function updateLine(key: number, field: keyof SOVLineItemCreate, value: string | number | null) {
    setLines((prev) =>
      prev.map((l) => (l._key === key ? { ...l, [field]: value } : l))
    );
  }

  function validate(): string | null {
    const hp = parseFloat(holdbackPercent);
    if (isNaN(hp) || hp < 0 || hp > 100)
      return 'Default holdback must be between 0 and 100';
    if (lines.length === 0) return 'At least one line item is required';
    for (const li of lines) {
      if (!li.description.trim()) return `Line ${li.line_number}: description is required`;
      if (!li.cost_code.trim()) return `Line ${li.line_number}: cost code is required`;
      if (!li.original_budget_amount || li.original_budget_amount <= 0)
        return `Line ${li.line_number}: budget must be > 0`;
    }
    const nums = lines.map((l) => l.line_number);
    if (new Set(nums).size !== nums.length) return 'Line numbers must be unique';
    return null;
  }

  async function handleSubmit() {
    setValidationError(null);
    const err = validate();
    if (err) {
      setValidationError(err);
      return;
    }

    createSOV.mutate(
      {
        holdback_percent: parseFloat(holdbackPercent),
        lender_name: lenderName || null,
        loan_number: loanNumber || null,
        line_items: lines.map(({ _key, ...rest }) => ({
          ...rest,
          original_budget_amount: Number(rest.original_budget_amount),
          holdback_percent: rest.holdback_percent != null ? Number(rest.holdback_percent) : null,
        })),
      },
      { onSuccess: onClose }
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingSov ? 'Replace Schedule of Values' : 'Create Schedule of Values'}
          </DialogTitle>
        </DialogHeader>

        {/* SOV Settings */}
        <div className="grid grid-cols-3 gap-4 mt-2">
          <div>
            <Label>Default Holdback %</Label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={holdbackPercent}
              onChange={(e) => setHoldbackPercent(e.target.value)}
              data-testid="sov-holdback-percent"
            />
          </div>
          <div>
            <Label>Lender Name (optional)</Label>
            <Input
              value={lenderName}
              onChange={(e) => setLenderName(e.target.value)}
              placeholder="e.g. TD Canada Trust"
            />
          </div>
          <div>
            <Label>Loan / Reference # (optional)</Label>
            <Input
              value={loanNumber}
              onChange={(e) => setLoanNumber(e.target.value)}
              placeholder="e.g. LON-2024-001"
            />
          </div>
        </div>

        {/* Line Items */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-base">Line Items</Label>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="sov-add-line">
              <PlusCircle className="mr-1 h-3 w-3" />
              Add Line
            </Button>
          </div>

          {lines.map((li, idx) => (
            <div
              key={li._key}
              className="grid grid-cols-12 gap-2 items-start border rounded-md p-2"
              data-testid={`sov-line-${idx}`}
            >
              {/* Line # */}
              <div className="col-span-1">
                <Label className="text-xs text-muted-foreground">#</Label>
                <Input
                  type="number"
                  min={1}
                  value={li.line_number}
                  onChange={(e) =>
                    updateLine(li._key, 'line_number', parseInt(e.target.value) || 1)
                  }
                  className="text-center"
                />
              </div>
              {/* Description */}
              <div className="col-span-4">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Input
                  value={li.description}
                  onChange={(e) => updateLine(li._key, 'description', e.target.value)}
                  placeholder="e.g. Site Preparation"
                />
              </div>
              {/* Cost Code */}
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground">Cost Code</Label>
                <Input
                  value={li.cost_code}
                  onChange={(e) => updateLine(li._key, 'cost_code', e.target.value)}
                  placeholder="01-100"
                />
              </div>
              {/* Budget */}
              <div className="col-span-3">
                <Label className="text-xs text-muted-foreground">Budget ($)</Label>
                <Input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={li.original_budget_amount || ''}
                  onChange={(e) =>
                    updateLine(li._key, 'original_budget_amount', parseFloat(e.target.value) || 0)
                  }
                />
              </div>
              {/* Holdback override */}
              <div className="col-span-1">
                <Label className="text-xs text-muted-foreground">Hold %</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={li.holdback_percent ?? ''}
                  placeholder="—"
                  onChange={(e) => {
                    const v = e.target.value;
                    updateLine(li._key, 'holdback_percent', v === '' ? null : parseFloat(v));
                  }}
                />
              </div>
              {/* Remove */}
              <div className="col-span-1 flex items-end pb-0.5">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive"
                  onClick={() => removeLine(li._key)}
                  data-testid={`sov-remove-line-${idx}`}
                  disabled={lines.length <= 1}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Validation error */}
        {validationError && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {validationError}
          </div>
        )}

        {/* Replace warning */}
        {existingSov && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
            Replacing the SOV will soft-delete the current version. Existing draw invoices referencing
            the old SOV will still show their original line items.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createSOV.isPending}
          >
            {createSOV.isPending ? 'Saving…' : existingSov ? 'Replace SOV' : 'Create SOV'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
